<template>
  <div class="p-field">
    <div class="p-field-horizontal">
      <label v-if="label" :for="id" class="p-text-secondary">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>
      <div class="p-tristatecheckbox-wrapper">
        <Checkbox
          ref="inputRef"
          :id="id"
          :modelValue="booleanValue"
          :indeterminate="isIndeterminate"
          :disabled="disabled"
          :required="required"
          :class="[
            'mop-tristatecheckbox',
            {
              'p-invalid': isInvalid,
              'p-disabled': disabled
            }
          ]"
          :aria-label="props['aria-label']"
          :aria-labelledby="props['aria-labelledby']"
          :aria-invalid="isInvalid"
          :aria-required="required"
          :aria-describedby="ariaDescribedBy"
          :pt="{
            root: { class: wrapperClass }
          }"
          @click="onClick"
          @blur="onBlur"
        />
        <label v-if="valueLabels" class="p-tristatecheckbox-label">
          {{ getValueLabel }}
        </label>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small
        v-for="(error, index) in localizedErrors"
        :key="index"
        :id="`${id}-error-${index}`"
        class="p-error block"
      >
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Checkbox from 'primevue/checkbox'

export default defineComponent({
  name: 'MOPTriStateCheckbox',
  components: {
    Checkbox
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import type { MOPTriStateCheckboxProps } from './types'

const props = withDefaults(defineProps<MOPTriStateCheckboxProps>(), {
  modelValue: null,
  disabled: false,
  required: false,
  valueLabels: undefined,
  label: undefined,
  helperText: '',
  invalid: false,
  id: () => `mop-tristatecheckbox-${Math.random().toString(36).substr(2, 9)}`,
  'aria-label': '',
  'aria-labelledby': '',
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  wrapperClass: undefined
})

const emit = defineEmits([
  'update:modelValue',
  'change',
  'validation-error',
  'validation-success'
])

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const inputRef = ref<any>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const booleanValue = computed(() => props.modelValue === true)
const isIndeterminate = computed(() => props.modelValue === null)

const getValueLabel = computed(() => {
  if (!props.valueLabels) return ''

  if (props.modelValue === true) return props.valueLabels.true || 'Yes'
  if (props.modelValue === false) return props.valueLabels.false || 'No'
  return props.valueLabels.null || 'Indeterminate'
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = props.modelValue

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onClick = async () => {
  if (props.disabled) return

  let newValue: boolean | null
  if (props.modelValue === null) {
    newValue = true
  } else if (props.modelValue === true) {
    newValue = false
  } else {
    newValue = null
  }

  emit('update:modelValue', newValue)
  emit('change', newValue)
  isDirty.value = true
  if (props.validateOnChange) {
    await validate()
  }
}

const onBlur = async () => {
  isDirty.value = true
  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }
  if (props.validateOnBlur) {
    await validate()
  }
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

defineExpose({
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>

